import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
import { BreadcrumbItem } from '../layouts/components/types';
import { useBreadcrumbMetadata } from '../contexts/BreadcrumbContext';
import { getBreadcrumbLabel, isUuidSegment } from '../router/config/breadcrumbRegistry';
import { useActiveRole } from './useActiveRole';
import { UserRole } from '@features/authentication/types';
import { MANAGE_PATH, MANAGE_ROLE_POLICY } from '@features/manage/constants';

const MAX_VISIBLE_DESKTOP = 5;
const MAX_VISIBLE_MOBILE = 2;

/**
 * Builds a flat breadcrumb array from the current pathname,
 * resolving static labels and dynamic (UUID) segments from metadata.
 */
const buildBreadcrumbs = (
  pathname: string,
  metadata: Record<string, string | undefined>
): BreadcrumbItem[] => {
  const segments = pathname.split('/').filter(Boolean);
  const crumbs: BreadcrumbItem[] = [];

  // Skip "app" prefix – it's a route wrapper, not a meaningful breadcrumb
  const startIndex = segments[0] === 'app' ? 1 : 0;

  let currentPath = segments[0] === 'app' ? '/app' : '';

  for (let i = startIndex; i < segments.length; i++) {
    const segment = segments[i];
    currentPath += `/${segment}`;

    const precedingSegment = i > startIndex ? segments[i - 1] : undefined;

    // For UUID segments that follow "questions" in a survey builder route, skip them
    // e.g. /app/surveys/:surveyId/questions -> we already show the survey title
    if (segment === 'questions' && i > startIndex && isUuidSegment(segments[i - 1])) {
      // "questions" in survey builder context is redundant since it's the survey detail
      // Skip adding it as a separate breadcrumb
      continue;
    }

    const label = getBreadcrumbLabel(segment, precedingSegment, metadata);

    crumbs.push({
      label,
      path: currentPath,
    });
  }

  return crumbs;
};

/**
 * Removes breadcrumb segments that are above the user's role-level
 * in the manage hierarchy so lower-privileged users don't see or
 * navigate to pages they can't access.
 */
const filterManageBreadcrumbs = (
  crumbs: BreadcrumbItem[],
  role: UserRole | null
): BreadcrumbItem[] => {
  if (!role) return crumbs;

  const policy = MANAGE_ROLE_POLICY[role];
  if (!policy) return crumbs; // SYSTEM_ADMIN or unmapped role -> no filtering

  const skipCount = policy.breadcrumbSkip;

  const manageIndex = crumbs.findIndex((c) => c.path === MANAGE_PATH);
  if (manageIndex === -1) return crumbs; // not a manage route

  const beforeManage = crumbs.slice(0, manageIndex);
  const manageCrumb = crumbs[manageIndex];
  const afterManage = crumbs.slice(manageIndex + 1);

  // Skip the first N segments after "Manage"
  const visibleAfterManage = afterManage.slice(Math.min(skipCount, afterManage.length));

  return [...beforeManage, manageCrumb, ...visibleAfterManage];
};

/**
 * Auto-squash breadcrumbs: if there are more than maxVisible items,
 * collapse middle items into a single "..." entry with collapsedItems.
 * Always keeps the first item and the last (maxVisible - 1) items visible.
 */
const squashBreadcrumbs = (crumbs: BreadcrumbItem[], maxVisible: number): BreadcrumbItem[] => {
  if (crumbs.length <= maxVisible) {
    return crumbs;
  }

  // Keep first item, collapse middle, keep last (maxVisible - 2) items
  const keepAtEnd = Math.max(maxVisible - 2, 1);
  const firstItem = crumbs[0];
  const collapsedItems = crumbs.slice(1, crumbs.length - keepAtEnd);
  const endItems = crumbs.slice(crumbs.length - keepAtEnd);

  return [
    firstItem,
    {
      label: '...',
      path: '',
      isCollapsed: true,
      collapsedItems,
    },
    ...endItems,
  ];
};

/**
 * Unified breadcrumb hook that replaces the old useBreadcrumbs.
 * Builds crumb array from pathname + metadata, applies auto-squash for long trails.
 */
export const useUnifiedBreadcrumbs = (): BreadcrumbItem[] => {
  const location = useLocation();
  const metadata = useBreadcrumbMetadata();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { currentRole } = useActiveRole();

  return useMemo(() => {
    const rawCrumbs = buildBreadcrumbs(location.pathname, metadata);
    const filteredCrumbs = filterManageBreadcrumbs(rawCrumbs, currentRole);

    const maxVisible = isMobile ? MAX_VISIBLE_MOBILE : MAX_VISIBLE_DESKTOP;
    return squashBreadcrumbs(filteredCrumbs, maxVisible);
  }, [location.pathname, metadata, isMobile, currentRole]);
};
